import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Transaction {
  id: string
  type: string
  amount: number
  currency: string
  payment_method: string
  customer_email: string
  product: string
  status: string
  created_at: string
}

interface Segment {
  id: string
  name: string
  description: string
  customer_count: number
  total_revenue: number
  avg_revenue_per_customer: number
  churn_rate: number
  growth_rate: number
}

export default function RevenueAnalyticsPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [segments, setSegments] = useState<Segment[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [forecasts, setForecasts] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [transRes, segmentsRes, statsRes, forecastRes] = await Promise.all([
      fetch(API_BASE + '/revenue/transactions'),
      fetch(API_BASE + '/revenue/segments'),
      fetch(API_BASE + '/revenue/statistics'),
      fetch(API_BASE + '/revenue/forecasts')
    ])
    if (transRes.ok) setTransactions((await transRes.json()).transactions || [])
    if (segmentsRes.ok) setSegments((await segmentsRes.json()).segments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (forecastRes.ok) setForecasts((await forecastRes.json()).forecasts || [])
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'refunded': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Revenue Analytics</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue Today</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.revenue_today)}</div>
              <div className={`text-xs ${statistics.revenue_change_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {statistics.revenue_change_pct >= 0 ? '+' : ''}{statistics.revenue_change_pct}% vs yesterday
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Monthly Revenue</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.revenue_this_month)}</div>
              <div className={`text-xs ${statistics.monthly_growth_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {statistics.monthly_growth_pct >= 0 ? '+' : ''}{statistics.monthly_growth_pct}% growth
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">MRR</div>
              <div className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.mrr)}</div>
              <div className="text-xs text-gray-500">Monthly Recurring Revenue</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">ARR</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.arr)}</div>
              <div className="text-xs text-gray-500">Annual Recurring Revenue</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-8">
          {forecasts.map(forecast => (
            <div key={forecast.period} className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500 capitalize mb-2">{forecast.period} Forecast</div>
              <div className="text-2xl font-bold text-blue-600 mb-1">{formatCurrency(forecast.predicted_revenue)}</div>
              <div className="text-xs text-gray-500 mb-3">
                Range: {formatCurrency(forecast.confidence_low)} - {formatCurrency(forecast.confidence_high)}
              </div>
              <div className={`text-sm ${forecast.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {forecast.growth_rate >= 0 ? '+' : ''}{forecast.growth_rate}% projected growth
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Transactions</h2></div>
            <div className="divide-y">
              {transactions.slice(0, 10).map(trans => (
                <div key={trans.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{trans.product}</div>
                      <div className="text-sm text-gray-500">{trans.customer_email}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${trans.type === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                        {trans.type === 'refund' ? '-' : '+'}{formatCurrency(Math.abs(trans.amount), trans.currency)}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(trans.status)}`}>{trans.status}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{trans.payment_method}</span>
                    <span>{new Date(trans.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Customer Segments</h3>
            <div className="space-y-4">
              {segments.map(segment => (
                <div key={segment.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{segment.name}</span>
                    <span className={`text-sm ${segment.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {segment.growth_rate >= 0 ? '+' : ''}{segment.growth_rate}%
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mb-2">{segment.customer_count} customers</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Revenue: </span>
                      <span className="font-medium">{formatCurrency(segment.total_revenue)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Churn: </span>
                      <span className="font-medium">{segment.churn_rate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
